import { ChangeEvent, CSSProperties, FocusEvent, ReactNode } from "react"
import { NavLink, Outlet, useNavigate } from "react-router-dom"
import classNames from "classnames"

import { ApiTypes, UITypes } from "types"
import commonStyles from "assets/scss/index.module.scss"

import { convertDataToOptions } from "utils/convertData"
import { formatPhoneNumber, normalizePhoneNumber } from "utils/phoneNumber"
import { useUserProfile } from "hooks/useUserProfile"
export {
  enterpriseWebsiteValidationMessage,
  getEnterpriseWebsiteValidationError,
  isEnterpriseWebsiteValid,
  normalizeEnterpriseWebsiteUrl,
} from "utils/enterpriseWebsite"

import { getAccountSettingsTabs } from "./config"
import { AccountSettingsTabGuardProvider } from "./navigationGuard"

import styles from "./index.module.scss"

export type ProfileValues = {
  companyName: string
  address1: string
  address2: string
  country: UITypes.Option[]
  zip: UITypes.Option[]
  state: UITypes.Option[]
  city: UITypes.Option[]
  suburb: UITypes.Option[]
}

export type AdditionalValues = {
  email: string
  enterpriseUrl: string
  fullName: string
  phoneNumber: string
  image: File | undefined
}

type FieldProps = {
  label: string
  name: string
  value: string
  placeholder?: string
  type?: "password" | "text"
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  className?: string
  errorMessage?: string
  disabled?: boolean
}

export const Field = ({
  label,
  name,
  value,
  placeholder,
  type = "text",
  onChange,
  onBlur,
  className,
  errorMessage,
  disabled,
}: FieldProps) => (
  <label className={classNames(styles.field, className)}>
    <span className={styles.label}>{label}</span>
    <input
      className={classNames(styles.input, { [commonStyles.error]: errorMessage })}
      name={name}
      value={value}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
      onChange={onChange}
      onBlur={onBlur}
    />
    {errorMessage && <span className={commonStyles.errorMessage}>{errorMessage}</span>}
  </label>
)

export const AccountPageShell = ({ children, footer }: { children: ReactNode; footer?: ReactNode }) => (
  <section className={styles.panel}>
    <div className={styles.panelBody}>{children}</div>
    {footer}
  </section>
)

export const AccountRoutePanel = ({ children }: { children: ReactNode }) => (
  <section className={styles.panel}>{children}</section>
)

export const AccountPageLayout = () => {
  const { userRole } = useUserProfile()
  const accountTabs = getAccountSettingsTabs(userRole)

  return (
    <AccountSettingsTabGuardProvider tabs={accountTabs}>
      {handleTabClick => (
        <main className={styles.page}>
          <nav
            className={styles.tabs}
            style={{ "--tabs-count": accountTabs.length } as CSSProperties}
            aria-label="Account settings"
          >
            {accountTabs.map(tab => (
              <NavLink
                key={tab.path}
                className={({ isActive }) => classNames(styles.tab, { [styles.tabActive]: isActive })}
                to={tab.path}
                onClick={event => handleTabClick(event, tab)}
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
          <Outlet />
        </main>
      )}
    </AccountSettingsTabGuardProvider>
  )
}

export const getEnterpriseProfileInitialState = (
  enterprise?: ApiTypes.Model.Enterprise,
  country?: ApiTypes.Model.Country | undefined,
): ProfileValues => ({
  companyName: enterprise?.name || "",
  address1: enterprise?.address1 || "",
  address2: enterprise?.address2 || "",
  country: country ? [{ value: country.id, label: country.name }] : [],
  zip: enterprise?.zip ? convertDataToOptions({ data: [enterprise.zip] }, "id", "code") : [],
  state: enterprise?.state ? convertDataToOptions({ data: [enterprise.state] }, "id", "name") : [],
  city: enterprise?.city ? convertDataToOptions({ data: [enterprise.city] }, "id", "name") : [],
  suburb: enterprise?.suburb ? convertDataToOptions({ data: [enterprise.suburb] }, "id", "name") : [],
})

export const getEnterpriseAdditionalInitialState = (enterprise?: ApiTypes.Model.Enterprise): AdditionalValues => ({
  email: enterprise?.email || "",
  enterpriseUrl: enterprise?.url || "",
  fullName: enterprise?.full_name || "",
  phoneNumber: enterprise?.tel1 ? formatPhoneNumber(enterprise.tel1) : "",
  image: undefined,
})

export const appendEnterpriseValue = (formData: FormData, name: string, value?: string | number | boolean | null) => {
  if (value === undefined || value === null) return

  formData.append(name, String(value))
}

const normalizeUrl = (value?: string) => {
  if (!value) return value

  return /^(http:\/\/|https:\/\/)/.test(value) ? value : `https://${value}`
}

export const normalizeComparableUrl = (value?: string) => normalizeUrl(value?.trim()) || ""

export const prepareEnterpriseAccountFormData = ({
  enterprise,
  country,
  profileValues,
  additionalValues,
}: {
  enterprise: ApiTypes.Model.Enterprise
  country?: ApiTypes.Model.Country
  profileValues?: ProfileValues
  additionalValues?: AdditionalValues
}) => {
  const formData = new FormData()
  const hasProfileValues = Boolean(profileValues)
  const profile = profileValues ?? getEnterpriseProfileInitialState(enterprise, country)
  const additional = additionalValues ?? getEnterpriseAdditionalInitialState(enterprise)
  const normalizedUrl = normalizeComparableUrl(additional.enterpriseUrl)
  const countryValue = hasProfileValues ? (profile.country[0]?.value ?? "") : (country?.id ?? enterprise.country?.id)
  const zipValue = hasProfileValues ? (profile.zip[0]?.value ?? "") : enterprise.zip?.id
  const cityValue = hasProfileValues ? (profile.city[0]?.value ?? "") : enterprise.city?.id
  const suburbValue = hasProfileValues ? (profile.suburb[0]?.value ?? "") : enterprise.suburb?.id
  const stateValue = hasProfileValues ? (profile.state[0]?.value ?? "") : enterprise.state?.id

  appendEnterpriseValue(formData, "name", profile.companyName.trim())
  appendEnterpriseValue(formData, "address1", profile.address1.trim())
  appendEnterpriseValue(formData, "address2", profile.address2.trim())
  appendEnterpriseValue(formData, "country", countryValue)
  appendEnterpriseValue(formData, "zip", zipValue)
  appendEnterpriseValue(formData, "city", cityValue)
  appendEnterpriseValue(formData, "suburb", suburbValue)
  appendEnterpriseValue(formData, "state", stateValue)
  appendEnterpriseValue(formData, "full_name", additional.fullName.trim())
  appendEnterpriseValue(formData, "email", additional.email.trim())
  appendEnterpriseValue(formData, "tel1", normalizePhoneNumber(additional.phoneNumber))
  appendEnterpriseValue(formData, "url", normalizedUrl)
  appendEnterpriseValue(formData, "is_enabled", enterprise.is_enabled ? 1 : 0)
  formData.append("contractor_ids", JSON.stringify(enterprise.contractor_ids ?? []))

  if (additional.image) {
    formData.append("image", additional.image)
  }

  return formData
}

export const isEnterpriseProfileDirty = (
  enterprise: ApiTypes.Model.Enterprise,
  country: ApiTypes.Model.Country | undefined,
  profileValues: ProfileValues,
) =>
  profileValues.companyName.trim() !== String(enterprise.name ?? "") ||
  profileValues.address1.trim() !== String(enterprise.address1 ?? "") ||
  profileValues.address2.trim() !== String(enterprise.address2 ?? "") ||
  String(profileValues.country[0]?.value ?? "") !== String(country?.id ?? enterprise.country?.id ?? "") ||
  String(profileValues.zip[0]?.value ?? "") !== String(enterprise.zip?.id ?? "") ||
  String(profileValues.city[0]?.value ?? "") !== String(enterprise.city?.id ?? "") ||
  String(profileValues.state[0]?.value ?? "") !== String(enterprise.state?.id ?? "") ||
  (profileValues.country[0]?.value === 3 &&
    String(profileValues.suburb?.[0]?.value ?? profileValues.city[0]?.value ?? "") !==
      String(enterprise.suburb?.id ?? enterprise.city?.id ?? ""))

export const isEnterpriseAdditionalDirty = (
  enterprise: ApiTypes.Model.Enterprise,
  additionalValues: AdditionalValues,
) =>
  additionalValues.fullName.trim() !== String(enterprise.full_name ?? "") ||
  additionalValues.email.trim() !== String(enterprise.email ?? "") ||
  normalizePhoneNumber(additionalValues.phoneNumber) !== normalizePhoneNumber(enterprise.tel1 ?? "") ||
  normalizeComparableUrl(additionalValues.enterpriseUrl) !== normalizeComparableUrl(enterprise.url) ||
  Boolean(additionalValues.image)

export const AccountCancelButton = ({ disabled = false }: { disabled?: boolean }) => {
  const navigate = useNavigate()

  return (
    <button className={styles.button} type="button" onClick={() => navigate(-1)} disabled={disabled}>
      Cancel
    </button>
  )
}

export const Footer = ({
  form,
  isLoading,
  isSaveDisabled,
  onSave,
  saveButtonType = "button",
}: {
  form?: string
  isLoading: boolean
  isSaveDisabled: boolean
  onSave?: () => void
  saveButtonType?: "button" | "submit"
}) => (
  <div className={styles.footer}>
    <AccountCancelButton disabled={isLoading} />
    <button
      className={classNames(styles.button, styles.saveButton)}
      form={form}
      type={saveButtonType}
      onClick={onSave}
      disabled={isLoading || isSaveDisabled}
    >
      {isLoading ? "Saving..." : "Save"}
    </button>
  </div>
)
